import { useState, useEffect } from 'react';
import { useSpring, useMotionValueEvent } from 'motion/react';

/** Vibrant Minimalism healthcare design tokens */
export const ADMIN_COLORS = {
  // Primary — themed via CSS vars
  primary: 'var(--color-primary-500)',
  primarySoft: 'color-mix(in srgb, var(--color-primary-500) 15%, transparent)',
  primaryText: 'var(--color-primary-700)',
  // Forest — Completed / Verified
  forest: '#0A9548',
  forestSoft: 'rgba(10, 149, 72, 0.12)',
  forestText: '#076B34',
  // Leaf — Growth / Progress / Vitality
  leaf: '#70BF4A',
  leafSoft: 'rgba(112, 191, 74, 0.12)',
  leafText: '#4A8A2E',
  // Text hierarchy
  navy: 'var(--admin-page-heading, #1E293B)',       // primary text (backward compat alias)
  textSecondary: 'var(--color-neutral-600)',
  textTertiary: 'var(--color-neutral-500)',
  // Surfaces
  surface: 'rgba(255, 255, 255, 0.45)',
  ice: 'rgba(248, 250, 252, 0.35)',
  // Borders
  border: 'rgba(148, 163, 184, 0.3)',
  borderMedium: 'rgba(148, 163, 184, 0.4)',
  // Shadows (primary-tinted)
  shadow: '0 4px 16px color-mix(in srgb, var(--color-primary-500) 8%, transparent)',
  shadowMd: '0 8px 24px color-mix(in srgb, var(--color-primary-500) 10%, transparent)',
  shadowLg: '0 12px 32px color-mix(in srgb, var(--color-primary-500) 12%, transparent)',
  // Semantic
  signal: '#EF4444',
  success: '#0A9548',
  successBg: 'rgba(10, 149, 72, 0.12)',
  successText: '#076B34',
  warning: '#F59E0B',
  warningBg: 'rgba(245, 158, 11, 0.12)',
  warningText: '#92400E',
  dangerBg: 'rgba(239, 68, 68, 0.12)',
  dangerText: '#991B1B',
  infoBg: 'rgba(59, 130, 246, 0.12)',
  infoText: '#1E40AF',
} as const;

/** Motion stagger container variant */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

/** Motion stagger item variant */
export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 350, damping: 30 },
  },
};

/**
 * AnimatedNumber hook — physics-based spring via motion/react.
 * Animates FROM previous value (not always from 0), interruptible.
 * Respects prefers-reduced-motion.
 */
export function useAnimatedNumber(value: number): number {
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const spring = useSpring(0, { stiffness: 120, damping: 20, mass: 1 });
  const [display, setDisplay] = useState(prefersReduced ? value : 0);

  useEffect(() => {
    if (prefersReduced) {
      setDisplay(value);
      return;
    }
    spring.set(value);
  }, [value, spring, prefersReduced]);

  useMotionValueEvent(spring, 'change', (v) => {
    if (!prefersReduced) setDisplay(Math.round(v));
  });

  return display;
}

export { triggerHapticFeedback } from '@/utils/haptics';
